import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FaArrowLeft, FaChartColumn } from "react-icons/fa6";
import Handler from "../../../Helpers/Handler";
import { textCapitalize } from "../../../Helpers/Utils";
import { Skeleton } from "../../../Components/Needed";
import AttendanceCalendar from "../AttendanceCalendar";
import Pagination from "../../../Components/List/Pagination";
import useListParams from "../../../Helpers/useListParams";

const toInputDate = (value) => new Date(value).toISOString().slice(0, 10);
const asHours = (minutes) => {
    if (!minutes) return "-";
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    return hours ? `${hours}h ${rest}m` : `${rest}m`;
};
const SORTS = [
    { key: "name", label: "Name" },
    { key: "workedMinutes", label: "Most hours worked" },
    { key: "extraMinutes", label: "Most extra hours" },
    { key: "absentDays", label: "Most absent days" },
];

const AttendanceReport = () => {
    const navigate = useNavigate();
    // Page number in the URL path, filters in the query string - see Helpers/useListParams.
    const { page, setPage, filters, setFilter } = useListParams({ from: toInputDate(new Date(Date.UTC(new Date().getFullYear(), new Date().getMonth(), 1))), to: toInputDate(new Date()), hubId: "", search: "", sortBy: "name", fullOnly: false, standardHours: 8 });
    const { from, to, hubId, search, sortBy, fullOnly, standardHours } = filters;
    const setFrom = (value) => setFilter("from", typeof value === "function" ? value(from) : value);
    const setTo = (value) => setFilter("to", typeof value === "function" ? value(to) : value);
    const setHubId = (value) => setFilter("hubId", typeof value === "function" ? value(hubId) : value);
    const setSearch = (value) => setFilter("search", typeof value === "function" ? value(search) : value);
    const setSortBy = (value) => setFilter("sortBy", typeof value === "function" ? value(sortBy) : value);
    const setFullOnly = (value) => setFilter("fullOnly", typeof value === "function" ? value(fullOnly) : value);
    const setStandardHours = (value) => setFilter("standardHours", typeof value === "function" ? value(standardHours) : value);
    const [hubs, setHubs] = useState([]);
    const [count] = useState(10);
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({ list: [], total: 0, summary: {} });
    const [openStaff, setOpenStaff] = useState(null);

    const body = useCallback((extra = {}) => ({
        from: new Date(from).toISOString(),
        to: new Date(to).toISOString(),
        standardMinutes: Math.round(Number(standardHours || 8) * 60),
        ...(hubId ? { hubId } : {}),
        ...(search.trim() ? { search: search.trim() } : {}),
        ...extra,
    }), [from, to, hubId, search, standardHours]);

    useEffect(() => {
        (async () => {
            const response = await Handler({ method: "get", url: "/admin/hub/active" });
            if (response?.success) setHubs(response?.data?.data || []);
        })();
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        const response = await Handler({
            method: "post",
            url: `/admin/staff/attendance/report/${page - 1}?count=${count}`,
            data: body({ sortBy, fullOnly }),
        });
        if (response?.success) setData(response?.data?.data);
        else {
            setData({ list: [], total: 0, summary: {} });
            if (response?.data?.message && response?.data?.message !== "No data found") toast.error(textCapitalize(response.data.message));
        }
        setLoading(false);
    }, [page, count, body, sortBy, fullOnly]);

    useEffect(() => { load(); }, [load]);

    const openDetail = (row) => setOpenStaff(row);

    const summary = data?.summary || {};

    return (
        <div className="container-fluid px-0">
            <button type="button" className="btn btn-link text-decoration-none px-0 mb-2 d-inline-flex align-items-center gap-2" onClick={() => navigate("/attendance")}><FaArrowLeft /> Back to attendance</button>
            <div className="card border-0 shadow-sm rounded-4 mb-3">
                <div className="card-body p-4">
                    <h3 className="fw-bold mb-1 d-flex align-items-center gap-2"><FaChartColumn className="text-primary" /> Attendance report</h3>
                    <p className="text-muted small mb-3">Full attendance, hours worked and extra hours for any date range (up to 62 days). Extra hours are time worked beyond the standard day.</p>
                    <div className="row g-3 align-items-end">
                        <div className="col-6 col-md-3 col-xl-2">
                            <label className="form-label small fw-semibold">From</label>
                            <input type="date" className="form-control" value={from} max={to} onChange={(e) => { setFrom(e.target.value); setPage(1); }} />
                        </div>
                        <div className="col-6 col-md-3 col-xl-2">
                            <label className="form-label small fw-semibold">To</label>
                            <input type="date" className="form-control" value={to} min={from} onChange={(e) => { setTo(e.target.value); setPage(1); }} />
                        </div>
                        <div className="col-6 col-md-3 col-xl-2">
                            <label className="form-label small fw-semibold">Hub</label>
                            <select className="form-select" value={hubId} onChange={(e) => { setHubId(e.target.value); setPage(1); }}>
                                <option value="">All hubs</option>
                                {hubs.map((hub) => <option key={hub.hubId} value={hub.hubId}>{hub.hubId}{hub.area ? ` - ${hub.area}` : ""}</option>)}
                            </select>
                        </div>
                        {/* <div className="col-6 col-md-3 col-xl-2">
                            <label className="form-label small fw-semibold">Standard day (hours)</label>
                            <input type="number" min="1" max="24" step="0.5" className="form-control" value={standardHours} onChange={(e) => setStandardHours(e.target.value)} onBlur={() => setPage(1)} />
                        </div> */}
                        <div className="col-6 col-md-3 col-xl-2">
                            <label className="form-label small fw-semibold">Sort by</label>
                            <select className="form-select" value={sortBy} onChange={(e) => { setSortBy(e.target.value); setPage(1); }}>
                                {SORTS.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
                            </select>
                        </div>
                        {/* <div className="col-6 col-md-3 col-xl-2">
                            <label className="form-label small fw-semibold">Search</label>
                            <input type="text" className="form-control" placeholder="Name or ID" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
                        </div> */}
                        <div className="col-12">
                            <div className="form-check">
                                <input id="fullOnly" type="checkbox" className="form-check-input" checked={fullOnly} onChange={(e) => { setFullOnly(e.target.checked); setPage(1); }} />
                                <label htmlFor="fullOnly" className="form-check-label small">Only staff with full attendance</label>
                            </div>
                        </div>
                    </div>
                    <div className="d-flex flex-wrap gap-2 mt-3">
                        <span className="att-chip att-info">Staff<strong className="ms-2">{summary.staff || 0}</strong></span>
                        <span className="att-chip att-ok">Full attendance<strong className="ms-2">{summary.fullAttendance || 0}</strong></span>
                        <span className="att-chip att-muted">Hours worked<strong className="ms-2">{asHours(summary.workedMinutes)}</strong></span>
                        <span className="att-chip att-warn">Extra hours<strong className="ms-2">{asHours(summary.extraMinutes)}</strong></span>
                    </div>
                </div>
            </div>

            <div className="card border-0 shadow-sm rounded-4">
                <div className="card-body p-0">
                    {loading ? <div className="p-4"><Skeleton height={200} /></div> : (
                        <div className="table-responsive">
                            <table className="table align-middle mb-0">
                                <thead>
                                    <tr>
                                        <th>Staff</th><th>Full attendance</th><th>Present</th><th>Half</th><th>Leave</th><th>Absent</th>
                                        <th>Worked</th>
                                        {/* <th>Avg / day</th><th>Breaks</th><th>Extra</th> */}
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.list.length === 0 ? <tr><td colSpan={10} className="text-center text-muted py-4">No data for this range</td></tr> : null}
                                    {data.list.map((row) => (
                                        <tr key={row.staffId} role="button" onClick={() => openDetail(row)}>
                                            <td><div className="fw-semibold">{textCapitalize(row.name)}</div><div className="small text-muted">{row.employeeId || row.staffId}</div></td>
                                            <td>{row.fullAttendance ? <span className="att-pill att-ok">Yes</span> : <span className="att-pill att-muted">No</span>}</td>
                                            <td>{row.presentDays}</td>
                                            <td>{row.halfDays}</td>
                                            <td>{row.leaveDays}</td>
                                            <td>{row.absentDays}</td>
                                            <td>{asHours(row.workedMinutes)}</td>
                                            {/* <td>{asHours(row.avgWorkedMinutesPerDay)}</td>
                                            <td>{asHours(row.breakMinutes)}</td>
                                            <td>{asHours(row.extraMinutes)}</td> */}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    <div className="p-3"><Pagination current={page} total={data.total} pageSize={count} onPageChange={setPage} /></div>
                </div>
            </div>

            {openStaff ? (
                <div className="card border-0 shadow-sm rounded-4 mt-3">
                    <div className="card-body p-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="fw-bold mb-0">{textCapitalize(openStaff.name)} — day by day</h5>
                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setOpenStaff(null)}>Close</button>
                        </div>
                        <AttendanceCalendar key={openStaff.staffId} staffId={openStaff.staffId} embedded initialDate={from} />
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default AttendanceReport;
